import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';

interface PodcastBlockViewModel {
    Title?: string;
    Summary?: string;
    ThumbnailUrl?: string;
    ThumbnailAltText?: string;
    TitleLinkUrl?: string;
    AudioUrl?: string;
    SeeMoreLinkUrl?: string;
    SeeMoreText?: string;
}

@Component({
    selector: 'podcast-block',
    templateUrl: './podcast-block.component.html',
    styles: [`
        .swapa-podcast-block {
            display: flex;
            align-items: flex-start;
            gap: 35px;
        }

        .swapa-podcast-block-thumbnail {
            flex: 0 0 136px;
            width: 136px;
            height: 136px;
        }

        .swapa-podcast-block-thumbnail img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            display: block;
        }

        .swapa-podcast-block-body {
            flex: 1 1 auto;
            min-width: 0;
        }

        .swapa-podcast-block-label {
            font-family: "Nimbus Sans", Arial, sans-serif;
            font-weight: 400;
            font-size: 0.875rem;
            line-height: 0.875rem;
            color: #808080;
            text-transform: uppercase;
            margin-bottom: 0.5rem;
        }

        .swapa-podcast-block-title {
            font-family: "URW DIN Condensed", Arial, sans-serif;
            font-weight: 700;
            font-size: 1.625rem;
            line-height: 1.75rem;
            margin-bottom: 0.5rem;
            overflow-wrap: anywhere;
        }

        .swapa-podcast-block-title a {
            color: #074F71;
            text-decoration: none;
        }

        .swapa-podcast-block-title a:hover,
        .swapa-podcast-block-title a:focus {
            text-decoration: underline;
        }

        .swapa-podcast-block-summary {
            font-family: "Nimbus Sans", Arial, sans-serif;
            font-weight: 400;
            font-size: 1rem;
            line-height: 1.25rem;
            color: #5A5A5A;
            margin-bottom: 0.75rem;
            overflow-wrap: anywhere;
        }

        /* The block's bottom edge is its last line: no trailing margin below it. */
        .swapa-podcast-block-title:last-child,
        .swapa-podcast-block-summary:last-child {
            margin-bottom: 0;
        }

        .swapa-podcast-block-actions {
            display: flex;
            align-items: center;
            gap: 16px;
        }

        .swapa-podcast-block-play {
            width: 31px;
            height: 31px;
            flex-shrink: 0;
            padding: 0;
            border: none;
            border-radius: 50%;
            background-color: #074F71;
            color: #fff;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
        }

        .swapa-podcast-block-play:hover,
        .swapa-podcast-block-play:focus {
            background-color: #0A6A96;
        }

        .swapa-podcast-block-play svg {
            width: 12px;
            height: 12px;
        }

        .swapa-podcast-block-see-more {
            font-family: "Nimbus Sans", Arial, sans-serif;
            font-size: 0.8125rem;
            color: #074F71;
        }

        /* Phones (portrait, or landscape - wide but short): title and summary on one line, truncated. Desktop and iPad show the full text. */
        @media (max-width: 767.98px), (max-height: 575.98px) and (orientation: landscape) {
            .swapa-podcast-block-title,
            .swapa-podcast-block-summary {
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
        }

        /* Tablets: a 1/3 column is only ~210px wide, so keep the thumbnail and gap smaller to leave room for the text. */
        @media (min-width: 768px) and (max-width: 991.98px) {
            .swapa-podcast-block {
                gap: 20px;
            }

            .swapa-podcast-block-thumbnail {
                flex-basis: 113px;
                width: 113px;
                height: 113px;
            }
        }

        /* Phones: thumbnail above the text, full width at its own aspect ratio and uncropped, like the
           featured image block. Landscape phones 768px and wider keep the side-by-side layout. */
        @media (max-width: 767.98px) {
            .swapa-podcast-block {
                flex-direction: column;
                align-items: stretch;
                gap: 20px;
            }

            .swapa-podcast-block-thumbnail {
                flex: none;
                width: 100%;
                height: auto;
            }

            .swapa-podcast-block-thumbnail img {
                height: auto;
                object-fit: initial;
            }
        }
    `],
})
export class PodcastBlockComponent implements OnInit, OnDestroy {
    public config: PodcastBlockViewModel | null = null;
    public isPlaying = false;

    @ViewChild('audio', { static: false }) private audioRef: ElementRef<HTMLAudioElement>;

    // Only one podcast should play at a time: when any other <audio> on the page starts, pause this one.
    private readonly onOtherAudioPlay = (event: Event) => {
        const audio = this.audioRef && this.audioRef.nativeElement;
        if (audio && event.target !== audio && !audio.paused) {
            audio.pause();
        }
    };

    constructor(private host: ElementRef<HTMLElement>) { }

    ngOnInit(): void {
        // The server sends the whole block as JSON in an attribute; attributes survive Angular taking over the host element.
        const json = this.host.nativeElement.getAttribute('config');
        if (!json) {
            return;
        }

        try {
            this.config = JSON.parse(json) as PodcastBlockViewModel;
        } catch {
            this.config = null;
            return;
        }

        // 'play' does not bubble, so listen in the capture phase.
        document.addEventListener('play', this.onOtherAudioPlay, true);
    }

    ngOnDestroy(): void {
        document.removeEventListener('play', this.onOtherAudioPlay, true);
    }

    togglePlay(): void {
        const audio = this.audioRef && this.audioRef.nativeElement;
        if (!audio) {
            return;
        }

        if (audio.paused) {
            audio.play().catch((err) => {
                this.isPlaying = false;
            });
        } else {
            audio.pause();
        }
    }
}
